//----- Component --------------------------------------------------------/
// Developer : Daiana Curcio
// Creation Date : 15/08/2025
// Version : 2
// Description : Dialog para historial derivacion
//------Changes ----------------------------------------------------------//
// Change Date : 04/11/2026
// Change by :
// Change description: Rediseño, origen de la derivación y estados de carga
//========================================================================/
import React, { useEffect, useState } from "react";
import { UrlApi } from "../../services/apirest";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import moment from "moment";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";

function Historial_derivaciones(props) {
  const { abrirDialogHistorial, row, onClose } = props;

  const [historial, setHistorial] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let vigente = true;

    setCargando(true);
    setError("");

    fetch(
      `${UrlApi}/viajes_abastecimiento_historial/historial/${row.id_viaje}`
    )
      .then((data) => data.json())
      .then((data) => {
        if (!vigente) return;

        if (Array.isArray(data)) {
          setHistorial(data);
        } else {
          setHistorial([]);
          setError("No se pudo cargar el historial.");
        }
      })
      .catch((err) => {
        console.error(err);
        if (vigente) {
          setHistorial([]);
          setError("No se pudo conectar con el servidor.");
        }
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });

    return () => {
      vigente = false;
    };
  }, [row.id_viaje]);

  const derivaciones = [...historial].sort(
    (a, b) => b.id_viaje_historial - a.id_viaje_historial
  );

  return (
    <Dialog
      open={abrirDialogHistorial}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" component="div">
            Historial de derivaciones
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Viaje N° {row.id_viaje}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {cargando ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : null}

        {!cargando && error ? <Alert severity="error">{error}</Alert> : null}

        {!cargando && !error && derivaciones.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
            No hay derivaciones registradas.
          </Typography>
        ) : null}

        {!cargando && !error && derivaciones.length > 0 ? (
          <Stack spacing={0}>
            {derivaciones.map((derivacion, index) => {
              const esActual = index === 0;
              const esUltimaFila = index === derivaciones.length - 1;

              return (
                <Box
                  key={derivacion.id_viaje_historial}
                  sx={{ display: "flex", gap: 2 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        color: "white",
                        bgcolor: esActual ? "primary.main" : "grey.500",
                      }}
                    >
                      <AltRouteIcon fontSize="small" />
                    </Box>
                    {!esUltimaFila ? (
                      <Box
                        sx={{
                          width: 2,
                          flexGrow: 1,
                          minHeight: 16,
                          bgcolor: "divider",
                        }}
                      />
                    ) : null}
                  </Box>

                  <Box sx={{ flexGrow: 1, pb: esUltimaFila ? 0 : 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                        minHeight: 36,
                      }}
                    >
                      <Typography color="text.secondary">
                        {derivacion.sucursal_anterior || "Origen no registrado"}
                      </Typography>
                      <ArrowForwardIcon fontSize="small" color="action" />
                      <Typography fontWeight="bold">
                        {derivacion.destino_derivacion || "Sin destino"}
                      </Typography>
                      {esActual ? (
                        <Chip
                          label="Actual"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : null}
                    </Box>

                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      sx={{ columnGap: 2, rowGap: 0.5, mt: 0.5 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <EventIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {derivacion.fecha_derivacion
                            ? moment(derivacion.fecha_derivacion).format(
                              "DD/MM/YYYY HH:mm"
                            )
                            : "Sin fecha"}
                        </Typography>
                      </Box>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {derivacion.usuario_derivacion || "Sin usuario"}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default Historial_derivaciones;